import { faRefresh } from '@fortawesome/free-solid-svg-icons';

import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { AuthService, NotificationService } from '@core/services';

import { LoginData } from '@shared/types';

import { environment } from '@env';
import { stringifyErr } from '@shared/utils';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false,
})
export class LoginComponent implements OnInit {
    public errorMessage?: string;

    public loginForm = new FormGroup({
        email: new FormControl('', [Validators.required, Validators.email]),
        password: new FormControl('', [Validators.required]),
    });

    public busy = false;
    public loadingIcon = faRefresh;

    constructor(
        private authService: AuthService,
        private notificationService: NotificationService,
        private route: ActivatedRoute,
        private router: Router,
    ) {}

    ngOnInit(): void {
        if (this.authService.activeUser.getValue() != undefined) {
            this.router.navigateByUrl(this.authService.popLoginRedirect());
        } else if (this.route.snapshot.queryParams.code != undefined) {
            this.silverfinCodeLogin(this.route.snapshot.queryParams.code);
        } else if (this.route.snapshot.queryParams.error === 'user-exists') {
            if (this.route.snapshot.queryParams.firm !== undefined) {
                this.notificationService.errorMessage(
                    $localize`:@@login.error.userExistsDifferentFirm:Deze gebruiker werd aangemaakt via de Silverfinomgeving ${this.route.snapshot.queryParams.firm}, gelieve deze omgeving te kiezen in het autorisatiescherm van Silverfin.`,
                );
            } else {
                this.notificationService.errorMessage(
                    $localize`:@@login.error.userAlreadyExists:Je kan niet inloggen via Silverfin aangezien er voor dit e-mailadres reeds een gebruiker met een andere loginmethode bestaat.`,
                );
            }
        } else if (this.route.snapshot.queryParams.error !== undefined) {
            this.notificationService.errorMessage(
                $localize`:@@login.error.externalNotPossible:Er ging iets mis bij het inloggen met extern account`,
            );
        }
    }

    onSubmit(): void {
        this.busy = true;
        let data = this.loginForm.value as LoginData;
        this.authService.login(data).subscribe({
            next: () => {
                this.router.navigateByUrl(this.authService.popLoginRedirect());
                this.busy = false;
            },
            error: (err) => {
                console.error('Something went wrong while logging in:', err);
                if ((err.error?.non_field_errors?.length ?? 0) > 0 && err.error.non_field_errors[0].includes('veri')) {
                    this.notificationService.errorMessage(
                        $localize`:@@login.error.emailNotVerified:Je e-mailadres moet eerst geverifieerd worden vooraleer je kan inloggen`,
                    );
                } else {
                    this.notificationService.errorMessage(
                        $localize`:@@login.error.notPossible:Inloggen is niet mogelijk met de opgegeven inloggegevens`,
                    );
                }
                this.busy = false;
            },
        });
    }

    silverfinCodeLogin(code: string): void {
        this.busy = true;
        this.authService.loginWithCode(code).subscribe({
            next: () => {
                this.router.navigateByUrl(this.authService.popLoginRedirect());
                this.busy = false;
            },
            error: (err) => {
                console.error('Something went wrong while logging in through silverfin:', err);
                if ((err.error?.non_field_errors?.length ?? 0) > 0 && err.error.non_field_errors[0].includes('veri')) {
                    this.notificationService.errorMessage(
                        $localize`:@@login.error.emailNotVerified:Je e-mailadres moet eerst geverifieerd worden vooraleer je kan inloggen`,
                    );
                } else {
                    let msg = $localize`:@@silverfinLogin.error.notPossible:Inloggen via Silverfin is momenteel niet mogelijk`;
                    if (err.status == 400) {
                        msg += `: ${stringifyErr(err)}`;
                    }
                    this.notificationService.errorMessage(msg);
                }
                this.busy = false;
            },
        });
    }

    silverfinLoginRedirect() {
        window.location.href = environment.endpoints.silverfinLogin;
    }
}
